import {
	alpha,
	Box,
	InputBase,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { Sparkles } from "lucide-react";
import type { ChangeEvent, ReactNode, Ref } from "react";

export type FlatRowInputProps = {
	label: string;
	value?: string;
	onChange?: (value: string) => void;
	onBlur?: () => void;
	inputRef?: Ref<HTMLInputElement>;
	placeholder?: string;
	suffix?: string;
	icon?: ReactNode;
	readOnly?: boolean;
	isLast?: boolean;
	ocrFilled?: boolean;
	inputMode?: "text" | "numeric" | "decimal" | "tel" | "email";
	type?: "text" | "number";
	error?: boolean;
	helperText?: string;
};

export const FlatRowInput = ({
	label,
	value,
	onChange,
	onBlur,
	inputRef,
	placeholder,
	suffix,
	icon,
	readOnly,
	isLast,
	ocrFilled,
	inputMode,
	type = "text",
	error,
	helperText,
}: FlatRowInputProps) => {
	const theme = useTheme();

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		onChange?.(event.target.value);
	};

	return (
		<Box>
			<Stack
				component="label"
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{
					width: "100%",
					px: 2,
					minHeight: 48,
					cursor: readOnly ? "pointer" : "text",
					borderBottom: isLast
						? "none"
						: `1px solid ${alpha(theme.palette.deprecated.grey[100], 0.6)}`,
					"&:active": readOnly
						? { bgcolor: theme.palette.deprecated.grey[50] }
						: undefined,
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					gap={0.6}
					flexShrink={0}
					mr={2}
				>
					<Typography
						variant="deprecatedBody1"
						color={
							error
								? theme.palette.deprecated.error.main
								: theme.palette.deprecated.grey[500]
						}
					>
						{label}
					</Typography>
					{ocrFilled && (
						<Sparkles size={14} color={theme.palette.primary.light} />
					)}
				</Stack>

				<Stack direction="row" alignItems="center" gap={0.6} minWidth={0}>
					{readOnly ? (
						<Typography
							variant="deprecatedBodyMedium"
							color={
								value
									? theme.palette.primary.main
									: theme.palette.deprecated.grey[300]
							}
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						>
							{value || placeholder || ""}
						</Typography>
					) : (
						<InputBase
							value={value ?? ""}
							onChange={handleChange}
							onBlur={onBlur}
							inputRef={inputRef}
							placeholder={placeholder}
							type={type}
							inputProps={{ inputMode }}
							sx={{
								flex: 1,
								minWidth: 0,
								"& input": {
									p: 0,
									textAlign: "right",
									fontSize: 14,
									fontWeight: 500,
									color: theme.palette.primary.main,
									"&::placeholder": {
										color: theme.palette.deprecated.grey[300],
										fontWeight: 400,
										opacity: 1,
									},
								},
							}}
						/>
					)}
					{suffix && (
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[400]}
							flexShrink={0}
						>
							{suffix}
						</Typography>
					)}
					{icon && <Box flexShrink={0}>{icon}</Box>}
				</Stack>
			</Stack>
			{error && helperText && (
				<Typography
					variant="deprecatedCaption"
					color={theme.palette.deprecated.error.main}
					px={2}
					pb={0.6}
				>
					{helperText}
				</Typography>
			)}
		</Box>
	);
};
